import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Option } from 'slim-select/dist/data.d';
import { Controller, useForm } from 'react-hook-form';

import Modal from '../../../components/modal/Modal';
import Button from '../../../../components/form/button/Button';
import Input from '../../../../components/form/input/Input';
import { Color } from '../../../../core/enums/Color';
import { Project, ProjectStatus } from '../../../../core/state/project/types';
import { spawnGlobalNotification } from '../../../../core/state/global-notifications/asyncActions';
import { GlobalNotificationTypes } from '../../../../core/enums/GlobalNotificationTypes';
import Select from '../../../../components/form/select/Select';
import { RootState } from '../../../../core/state/state';
import TextArea from '../../../../components/form/text-area/TextArea';
import Radio from '../../../../components/form/radio/Radio';
import { createNewBarrier, editBarrierDevice, getBarriers } from '../../../../core/state/barriers/asyncActions';
import { BarrierFormData, BarrierLocationType } from '../../../../core/state/barriers/types';
import { ELEVEN_NUMBERS_REGEX, ONLY_NUMBERS_REGEX } from '../../../../core/constants/regex';
import { NotificationPosition } from '../../../../core/state/global-notifications/types';

import './CreateAndEditBarrierModal.scss';

interface Props {
  isOpen: boolean;
  closeModal: () => void;
  barrierId?: number;
}

const CreateAndEditBarrierModal: React.FC<Props> = ({ isOpen, closeModal, barrierId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { projects } = useSelector((state: RootState) => state.projects);
  const selectedBarrier = useSelector((state: RootState) => state.barriers.barriers?.find((barrier) => barrier.id === barrierId));

  const defaultFormValues = {
    serialNumber: selectedBarrier?.serialNumber ?? '',
    barrierGateSerialNumber: selectedBarrier?.barrierGateSerialNumber ?? '',
    inventoryNumber: selectedBarrier?.inventoryNumber ?? '',
    locationType: selectedBarrier?.locations[0].type ?? BarrierLocationType.None,
    locationId: selectedBarrier?.locations[0].id ?? 0,
    description: selectedBarrier?.description ?? ''
  };

  const projectsToOptions = (project: Project): Option => ({
    text: `${project.code} (${project.name})`,
    value: `${project.id}`
  });

  const projectOptions = useMemo(() => projects?.filter((item) => item.statusEnum === ProjectStatus.Active)?.map(projectsToOptions), [projects]);

  const validationSchema = yup
    .object()
    .shape({
      serialNumber: yup
        .string()
        .matches(ONLY_NUMBERS_REGEX, t('validation.onlyNumbers'))
        .test('minLength', t('validation.input.minFour'), (val: any) => {
          return val.toLocaleString('fullwide', { useGrouping: false }).length >= 4;
        })
        .test('len', t('validation.input.maxThirty'), (val: any) => {
          return val.toLocaleString('fullwide', { useGrouping: false }).length <= 30;
        })
        .typeError(t('validation.onlyNumbers')),
      barrierGateSerialNumber: yup
        .string()
        .matches(ELEVEN_NUMBERS_REGEX, { message: t('validation.input.elevenNumbers'), excludeEmptyString: true })
        .notRequired(),
      inventoryNumber: yup.string().max(7, t('validation.input.maxSeven')),
      locationType: yup.string(),
      locationId: yup.string().nullable(),
      description: yup.string().max(200, t('validation.input.max200')).nullable()
    })
    .required();

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors, isValid }
  } = useForm<BarrierFormData>({
    mode: 'onChange',
    defaultValues: defaultFormValues,
    resolver: yupResolver(validationSchema)
  });

  const locationOption = watch('locationType');
  const locationIdOption = watch('locationId');

  const onSubmit = async (formData: BarrierFormData) => {
    if (barrierId) {
      const response: boolean = await dispatch<any>(
        editBarrierDevice(barrierId, {
          ...formData,
          serialNumber: formData.serialNumber,
          locationId: Number(formData.locationId)
        })
      );
      if (response === false) {
        dispatch(
          spawnGlobalNotification({
            type: GlobalNotificationTypes.Error,
            postition: NotificationPosition.Right,
            message: t('barrierManagement.modal.edit.message.deliveryZone')
          })
        );
        closeModal();
      } else {
        dispatch(
          spawnGlobalNotification({
            type: GlobalNotificationTypes.Success,
            postition: NotificationPosition.Right,
            title: t('common.changesSaved'),
            message: t('barrierManagement.modal.edit.message.success.text', { serialNumber: selectedBarrier?.serialNumber })
          })
        );
        closeModal();
        dispatch(getBarriers());
      }
    } else {
      const response = await dispatch<any>(
        createNewBarrier({ ...formData, serialNumber: formData.serialNumber, locationId: Number(formData.locationId) })
      );

      if (response === false) {
        dispatch(
          spawnGlobalNotification({
            type: GlobalNotificationTypes.Error,
            postition: NotificationPosition.Right,
            message: t('barrierManagement.modal.createNew.message.serialNumber'),
            delay: 5000
          })
        );
      } else {
        dispatch(
          spawnGlobalNotification({
            type: GlobalNotificationTypes.Success,
            postition: NotificationPosition.Right,
            title: t('barrierManagement.modal.createNew.message.success.title'),
            message: t('barrierManagement.modal.createNew.message.success.text')
          })
        );
        closeModal();
      }
    }
    dispatch(getBarriers());
  };

  const closeBarrierModal = () => {
    closeModal();
    dispatch(
      spawnGlobalNotification({
        type: GlobalNotificationTypes.Info,
        postition: NotificationPosition.Right,
        title: t('common.changesDiscarded'),
        message: t(barrierId ? 'barrierManagement.modal.edit.message.cancel.text' : 'barrierManagement.modal.createNew.cancel.text'),
        delay: 5000
      })
    );
  };

  const projectDropdownOptions = useMemo(
    () => [
      {
        label: t('barrierManagement.modal.createNew.form.option.1'),
        value: BarrierLocationType.None
      },
      {
        label: t('barrierManagement.modal.createNew.form.option.2'),
        value: BarrierLocationType.WarehouseZell
      },
      {
        label: t('barrierManagement.modal.createNew.form.option.3'),
        value: BarrierLocationType.WarehouseNord
      },
      {
        label: t('barrierManagement.modal.createNew.form.option.4'),
        value: BarrierLocationType.Project
      }
    ],
    [t]
  );

  const buttons = (
    <div className="modal-buttons">
      <Button color={Color.Blue} buttonTypes={['transparent']} onClick={closeBarrierModal}>
        <span>{t('common.cancel')}</span>
      </Button>
      <Button
        disabled={!isValid || (locationOption === BarrierLocationType.Project && locationIdOption === 0)}
        form="create-and-edit-barrier"
        type="submit"
        buttonTypes={['blue']}
      >
        {t(barrierId ? 'common.save' : 'common.create')}
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      closeModal={closeBarrierModal}
      title={<h3 className="modal-title">{t(!barrierId ? 'barrierManagement.modal.createNew.title' : 'barrierManagement.modal.edit.title')}</h3>}
      toolbar={buttons}
      size="small"
    >
      <form id="create-and-edit-barrier" className="create-and-edit-barrier-modal" onSubmit={handleSubmit(onSubmit)}>
        <div className="create-and-edit-barrier-modal__content">
          <Controller
            name="serialNumber"
            control={control}
            render={({ field: { onChange, value, name } }) => (
              <Input
                className={`${barrierId && 'edit__input'}`}
                type="string"
                onChange={(e) => {
                  onChange(e.target.value.trim());
                }}
                id="serialNumber"
                error={errors.serialNumber?.message}
                name={name}
                disabled={typeof barrierId === 'number'}
                label={t('barrierManagement.heading.snControl')}
                infoText=" (Datafox) *"
                placeholder="e.g. 4578"
                value={value}
              />
            )}
          />
          <Controller
            name="barrierGateSerialNumber"
            control={control}
            render={({ field: { onChange, value, name } }) => (
              <Input
                label={t('barrierManagement.heading.snSystem')}
                placeholder="e.g. 12345678911"
                id="barrierGateSerialNumber"
                error={errors.barrierGateSerialNumber?.message}
                name={name}
                type="string"
                infoText=" (Acotec)"
                value={value}
                onChange={(e) => onChange(e.target.value)}
              />
            )}
          />
          <Controller
            name="inventoryNumber"
            control={control}
            render={({ field: { onChange, value, name } }) => (
              <Input
                label={t('barrierManagement.heading.inventoryNumber')}
                placeholder="#   e.g. 12345"
                id="inventoryNumber"
                error={errors.inventoryNumber?.message}
                name={name}
                type="string"
                infoText=" (Köster)"
                value={value}
                onChange={(e) => onChange(e.target.value)}
              />
            )}
          />
          <Controller
            name="locationType"
            control={control}
            render={({ field: { onChange, name, value } }) => (
              <Radio
                id="locationType"
                name={name}
                label={t('barrierManagement.modal.createNew.form.projectAssignment')}
                options={projectDropdownOptions}
                defaultValue={value}
                onChange={onChange}
              />
            )}
          />
          {locationOption === BarrierLocationType.Project && (
            <Controller
              name="locationId"
              control={control}
              render={({ field: { onChange, value, name } }) => (
                <Select
                  id="locationId"
                  name={name}
                  showSearch={false}
                  value={String(value)}
                  onChange={(option) => {
                    if (Array.isArray(option)) {
                      [option] = option; // eslint-disable-line no-param-reassign
                    }
                    onChange(option.value);
                  }}
                  options={projectOptions}
                  addPlaceholderOption
                  showErrorMessage
                  error={errors.locationId?.message}
                />
              )}
            />
          )}
          <Controller
            name="description"
            control={control}
            render={({ field: { onChange, value, name } }) => (
              <TextArea
                label={t('common.description')}
                id="description"
                error={errors.description?.message}
                name={name}
                value={value}
                onChange={(e) => onChange(e.target.value)}
              />
            )}
          />
        </div>
      </form>
    </Modal>
  );
};

export default CreateAndEditBarrierModal;
